import { useMemo } from 'react'

import { AxiosResponse } from 'axios'

import { Episode } from '../../../models/episodes/Episodes'
import { RiskFactorsResponse } from '../../../models/riskFactors/RiskFactors'
import { SearchRequestData, TriageSearch } from '../../../models/searchForCare/SearchForCare'
import { CENTER_OF_EXCELLENCE } from '../../centerOfExcellence/types'
import { COMMON_MIXPANEL_PAGE } from '../../common/constants/mixpanelConstants'
import { TimeUnit } from '../../common/utils/dateUtils'
import { isLanguageEnglish } from '../../intl/utils/isLanguageEnglish'
import { ManagerCoachingSeatEligibility } from '../../managerCoaching/seatEligibility'
import { useRunSpeculativeSearch } from '../../searchForCare/hooks/useRunSpeculativeSearch'
import { getTriageSearchesWithinPastTimeFrame } from '../../searchForCare/utils'
import {
  DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  ENTRYPOINT_ONLY_TOOLKITS,
  SELECT_ELIGIBLE_TOOLKITS,
  WFMH_ELIGIBLE_TOOLKITS,
} from '../constants'
import {
  PROVIDER_SUPPLY_GROUP,
  SpeculativeSearchThresholds,
  TOOLKIT_TYPE,
  ToolkitAvailabilityReason,
  WELLNESS_TOOLKIT_PROMOTION,
  WellnessToolkitAvailableConfig,
} from '../types'
import {
  evaluateShortCircuitEligibility,
  evaluateToolkitAvailability,
  getWellnessToolkitAvailableConfig,
  isLatestCompletedSearchNotTierThree,
  isNotInActiveEpisode as isNotInActiveEpisodeCheck,
  isNotSelfHarmOrSuicialIdeationIndicated,
} from '../wellnessToolkitUtil'

export type ToolkitAvailabilityState = {
  isToolkitAvailable: boolean
  isToolkitTriggered: boolean
  reasons: ToolkitAvailabilityReason[]
}

type ToolkitBaseConfig = {
  conditions: WellnessToolkitAvailableConfig
  configExtras?: WellnessToolkitAvailableConfig['configExtras']
  isFeatureFlagEnabled?: boolean
  areRequiredProgramsEnabled?: boolean
}

export interface useToolkitAvailabilityMapParams {
  isLyraSelect: boolean
  page?: COMMON_MIXPANEL_PAGE
  lyraId: string
  isEssentialsEnabled: boolean
  isCoachingEnabled: boolean
  isTherapyEnabled: boolean
  isCoachingForParentsEnabled: boolean
  hideChildSearchCTA?: boolean
  hideDependentField?: boolean
  isUserTeen: boolean
  triageSearches: TriageSearch[]
  episodes: Episode[]
  isUserLoggedIn: boolean
  isInternationalUser: boolean
  postSpeculativeSearch: (lyraId: string, searchData: SearchRequestData) => Promise<AxiosResponse<boolean>>
  riskFactors?: RiskFactorsResponse
  userDOB?: string
  userCountryName?: string
  userGender?: string
  displayLanguage?: string
  isFamilyHubEnabled?: boolean
  isCoachingEntryPointEnabled?: boolean
  isInternationalLifestyleCoachingEnabled?: boolean
  isCoachingForParentsEntryPointEnabled?: boolean
  isCriticalIncidentSupportToolkitEnabled?: boolean
  isNeurodiversityCOEEnabled?: boolean
  isHealthChallengesCOEEnabled?: boolean
  isWomensHealthToolkitEnabled?: boolean
  isManagerHubEnabled?: boolean
  /** LaunchDarkly `isPedsCOETriageLaunchEnabled` */
  isPediatricsCOETriageLaunchEnabled?: boolean
  /** Customer has all three Pediatrics COE programs enabled; derived by each app's existing selector */
  isPediatricsCOEProgramsEnabled?: boolean
  /** Whether customer and viewer data have loaded. Absent reads as still loading. */
  isEligibilityDataLoaded?: boolean
  /** Active `managerHubTriggeredAt` window state; produced by the Manager Hub trigger framework */
  isManagerHubTriggered?: boolean
  /** Seat state from the app's seat-based-licenses query; absent reads as unknown and never promotes */
  managerCoachingSeatEligibility?: ManagerCoachingSeatEligibility
  isSpeculativeFilterEnabled: boolean
  speculativeSearchThresholds?: SpeculativeSearchThresholds
  disableSelfCareTopics?: string
}

/**
 * Evaluates availability for all toolkits (WFMH or Lyra Select) and returns a map of availability states.
 *
 * This hook performs comprehensive eligibility checks for each toolkit including:
 * - Short-circuit checks (feature flags, user attributes, essentials/coaching enabled)
 * - Clinical safety checks (SH/SI, T3 recommendations, active episodes)
 * - Provider supply checks (speculative search for coaching toolkits)
 *
 * The hook optimizes performance by:
 * - Running short-circuit checks first to skip expensive operations when possible
 * - Batching speculative searches for all eligible toolkits in a single request
 * - Memoizing results based on input parameters
 *
 * @returns {Object} Object containing:
 *   - availabilityByToolkit: Record mapping each toolkit to its availability state and reasons
 *   - isLoading: Boolean indicating if speculative search is in progress
 */
export const useToolkitAvailabilityMap = ({
  isLyraSelect,
  page,
  lyraId,
  isEssentialsEnabled,
  isCoachingEnabled,
  isTherapyEnabled,
  isCoachingForParentsEnabled,
  hideChildSearchCTA,
  hideDependentField,
  isUserTeen,
  triageSearches,
  episodes,
  isUserLoggedIn,
  isInternationalUser,
  postSpeculativeSearch,
  riskFactors,
  userDOB,
  userCountryName,
  userGender,
  displayLanguage,
  isFamilyHubEnabled,
  isCoachingEntryPointEnabled,
  isInternationalLifestyleCoachingEnabled,
  isCoachingForParentsEntryPointEnabled,
  isCriticalIncidentSupportToolkitEnabled,
  isNeurodiversityCOEEnabled,
  isHealthChallengesCOEEnabled,
  isWomensHealthToolkitEnabled,
  isManagerHubEnabled,
  isManagerHubTriggered,
  isPediatricsCOETriageLaunchEnabled,
  isPediatricsCOEProgramsEnabled,
  isEligibilityDataLoaded,
  managerCoachingSeatEligibility,
  isSpeculativeFilterEnabled,
  speculativeSearchThresholds = DEFAULT_SPECULATIVE_FILTER_THRESHOLDS,
  disableSelfCareTopics,
}: useToolkitAvailabilityMapParams): {
  availabilityByToolkit: Record<TOOLKIT_TYPE, ToolkitAvailabilityState>
  isLoading: boolean
} => {
  const isUserDisplayLanguageEnglish = displayLanguage ? isLanguageEnglish(displayLanguage) : false

  const { eligibleToolkits, baseConfigByToolkit, passesShortCircuitByToolkit } = useMemo(() => {
    const featureFlagByToolkit: Partial<Record<TOOLKIT_TYPE, boolean>> = {
      [WELLNESS_TOOLKIT_PROMOTION.FAMILY]: isFamilyHubEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.WORK]: isCoachingEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.PURPOSE]: isCoachingEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.RELATIONSHIPS]: isCoachingEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.PARENT]: isCoachingForParentsEntryPointEnabled,
      [WELLNESS_TOOLKIT_PROMOTION.TRAUMA]: isCriticalIncidentSupportToolkitEnabled,
      [CENTER_OF_EXCELLENCE.NEURODIVERSITY]: isNeurodiversityCOEEnabled,
      [CENTER_OF_EXCELLENCE.HEALTH_CHALLENGES]: isHealthChallengesCOEEnabled,
      [CENTER_OF_EXCELLENCE.WOMENS_HEALTH]: isWomensHealthToolkitEnabled,
      [CENTER_OF_EXCELLENCE.MANAGER_HUB]: isManagerHubEnabled,
      [CENTER_OF_EXCELLENCE.PEDIATRICS]: isPediatricsCOETriageLaunchEnabled,
    }

    /** Toolkits gated on customer programs, resolved per toolkit the same way the feature flag is */
    const requiredProgramsEnabledByToolkit: Partial<Record<TOOLKIT_TYPE, boolean>> = {
      [CENTER_OF_EXCELLENCE.PEDIATRICS]: isPediatricsCOEProgramsEnabled,
    }

    /**
     * Entry-point-only toolkits are evaluated here so their entry points can read availability, but
     * `useAvailableToolkits` filters them out of the promoted lists. On Lyra Select they have no config,
     * so `getWellnessToolkitAvailableConfig` force-hides them.
     */
    const toolkits = [
      ...(isLyraSelect ? SELECT_ELIGIBLE_TOOLKITS : WFMH_ELIGIBLE_TOOLKITS),
      ...ENTRYPOINT_ONLY_TOOLKITS,
    ]

    const baseConfig: Partial<Record<TOOLKIT_TYPE, ToolkitBaseConfig>> = {}
    const shortCircuitMap: Partial<Record<TOOLKIT_TYPE, boolean>> = {}

    toolkits.forEach((toolkit) => {
      const conditions = getWellnessToolkitAvailableConfig({
        toolkit,
        isLyraSelect,
        page,
        isInternationalLifestyleCoachingEnabled,
      })
      const { configExtras } = conditions
      const isFeatureFlagEnabled = featureFlagByToolkit[toolkit]
      const areRequiredProgramsEnabled = requiredProgramsEnabledByToolkit[toolkit]

      baseConfig[toolkit] = {
        conditions,
        configExtras,
        isFeatureFlagEnabled,
        areRequiredProgramsEnabled,
      }

      const { hasPassedShortCircuitEligibilityChecks } = evaluateShortCircuitEligibility({
        toolkitLabel: toolkit,
        conditions,
        configExtras,
        isFeatureFlagEnabled,
        isEssentialsEnabled,
        isCoachingEnabled,
        isTherapyEnabled,
        isCoachingForParentsEnabled,
        hideChildSearchCTA,
        hideDependentField,
        isUserTeen,
        isInternationalUser,
        isUserDisplayLanguageEnglish,
        disableSelfCareTopics,
        managerCoachingSeatEligibility,
        areRequiredProgramsEnabled,
        isEligibilityDataLoaded,
      })

      shortCircuitMap[toolkit] = hasPassedShortCircuitEligibilityChecks
    })

    return {
      eligibleToolkits: toolkits,
      baseConfigByToolkit: baseConfig as Record<TOOLKIT_TYPE, ToolkitBaseConfig>,
      passesShortCircuitByToolkit: shortCircuitMap as Record<TOOLKIT_TYPE, boolean>,
    }
  }, [
    isFamilyHubEnabled,
    isCoachingEntryPointEnabled,
    isInternationalLifestyleCoachingEnabled,
    isCoachingForParentsEntryPointEnabled,
    isCriticalIncidentSupportToolkitEnabled,
    isNeurodiversityCOEEnabled,
    isHealthChallengesCOEEnabled,
    isWomensHealthToolkitEnabled,
    isManagerHubEnabled,
    isPediatricsCOETriageLaunchEnabled,
    isPediatricsCOEProgramsEnabled,
    isEligibilityDataLoaded,
    isLyraSelect,
    page,
    isEssentialsEnabled,
    isCoachingEnabled,
    isTherapyEnabled,
    isCoachingForParentsEnabled,
    hideChildSearchCTA,
    hideDependentField,
    isUserTeen,
    isInternationalUser,
    isUserDisplayLanguageEnglish,
    disableSelfCareTopics,
    managerCoachingSeatEligibility,
  ])

  const isNotSHOrSI = isNotSelfHarmOrSuicialIdeationIndicated(riskFactors)
  const isNotT3Recommendation = isLatestCompletedSearchNotTierThree(triageSearches)
  const triageSearchesLast3Months = useMemo(
    () => getTriageSearchesWithinPastTimeFrame(triageSearches, 3, TimeUnit.MONTHS),
    [triageSearches],
  )

  const parentCoachingSearchRequestData = useMemo(() => {
    const shouldHaveProviderSupply = baseConfigByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT]
    const programSearchRequestData =
      baseConfigByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT]?.configExtras?.programSearchRequestData

    if (
      !shouldHaveProviderSupply ||
      !passesShortCircuitByToolkit[WELLNESS_TOOLKIT_PROMOTION.PARENT] ||
      programSearchRequestData === undefined
    ) {
      return undefined
    }

    return programSearchRequestData
  }, [baseConfigByToolkit, passesShortCircuitByToolkit])

  const parentToolkitFilter = speculativeSearchThresholds
    ? speculativeSearchThresholds[PROVIDER_SUPPLY_GROUP.PARENT_COACHING]
    : undefined

  const parentSpeculativeSearchResult = useRunSpeculativeSearch({
    searchRequestData: parentCoachingSearchRequestData,
    isUserLoggedIn,
    lyraId,
    userDOB,
    userCountryName,
    isInternationalUser,
    postSpeculativeSearch,
    isSpeculativeFilterEnabled,
    ...(isSpeculativeFilterEnabled && parentToolkitFilter ? { filters: parentToolkitFilter } : {}),
  })

  const hasAvailableProviderSupply: Partial<Record<TOOLKIT_TYPE, boolean>> = useMemo(
    () => ({
      [WELLNESS_TOOLKIT_PROMOTION.PARENT]: parentSpeculativeSearchResult.hasAvailableSupply,
    }),
    [parentSpeculativeSearchResult],
  )

  const availabilityByToolkit = useMemo(() => {
    const result: Partial<Record<TOOLKIT_TYPE, ToolkitAvailabilityState>> = {}

    eligibleToolkits.forEach((toolkit) => {
      const baseConfig = baseConfigByToolkit[toolkit]
      if (!baseConfig) {
        return
      }

      const { conditions, configExtras, isFeatureFlagEnabled, areRequiredProgramsEnabled } = baseConfig

      const isNotInActiveEpisode = isNotInActiveEpisodeCheck(
        episodes,
        lyraId,
        configExtras?.allowedProgramsForActiveEpisodes,
      )

      const shouldHaveProviderSupply = conditions.shouldHaveProviderSupply && isUserLoggedIn

      const isToolkitTriggered =
        configExtras?.triggerConditionsChecker?.({
          triageSearches: triageSearchesLast3Months,
          userGender,
          isManagerHubTriggered,
          isInternationalUser,
          managerCoachingSeatEligibility,
        }) ?? false

      const { isToolkitAvailable, reasons } = evaluateToolkitAvailability({
        toolkitLabel: toolkit,
        conditions,
        configExtras,
        isFeatureFlagEnabled,
        isEssentialsEnabled,
        isCoachingEnabled,
        isTherapyEnabled,
        isCoachingForParentsEnabled,
        hideChildSearchCTA,
        hideDependentField,
        isUserTeen,
        isInternationalUser,
        isUserDisplayLanguageEnglish,
        disableSelfCareTopics,
        managerCoachingSeatEligibility,
        areRequiredProgramsEnabled,
        isEligibilityDataLoaded,
        isNotSHOrSI,
        isNotInActiveEpisode,
        isNotT3Recommendation,
        shouldHaveProviderSupply,
        hasAvailableProviderSupply: conditions.shouldHaveProviderSupply
          ? (hasAvailableProviderSupply[toolkit] ?? false)
          : false,
      })

      result[toolkit] = {
        isToolkitAvailable,
        isToolkitTriggered,
        reasons,
      }
    })

    return result as Record<TOOLKIT_TYPE, ToolkitAvailabilityState>
  }, [
    eligibleToolkits,
    baseConfigByToolkit,
    episodes,
    lyraId,
    isUserLoggedIn,
    isEssentialsEnabled,
    isCoachingEnabled,
    isTherapyEnabled,
    isCoachingForParentsEnabled,
    hideChildSearchCTA,
    hideDependentField,
    isUserTeen,
    isInternationalUser,
    isUserDisplayLanguageEnglish,
    disableSelfCareTopics,
    isEligibilityDataLoaded,
    isNotSHOrSI,
    isNotT3Recommendation,
    hasAvailableProviderSupply,
    triageSearchesLast3Months,
    userGender,
    isManagerHubTriggered,
    managerCoachingSeatEligibility,
  ])

  return {
    availabilityByToolkit,
    isLoading: parentSpeculativeSearchResult.isLoading,
  }
}
